import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Empty,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography
} from 'antd';
import {
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import {
    getActiveSpeciality,
    getMedicalRecordSections,
    postMedicalRecordSection,
    postSpeciality,
    putMedicalRecordSection,
    putSpeciality,
    putSpecialityDefaultSections
} from '../store/user/thunks';
import { MedicalRecordSection, Speciality } from '../store/user/types';

const { Title, Text } = Typography;

type ModalMode = 'speciality' | 'section';

const SpecialitySectionManagement: React.FC<any> = (props: any) => {
    const [specialityForm] = Form.useForm();
    const [sectionForm] = Form.useForm();
    const [specialities, setSpecialities] = useState<Speciality[]>([]);
    const [sections, setSections] = useState<MedicalRecordSection[]>([]);
    const [selectedSpecialityId, setSelectedSpecialityId] = useState<string | null>(null);
    const [selectedDefaultSectionIds, setSelectedDefaultSectionIds] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [modalMode, setModalMode] = useState<ModalMode | null>(null);
    const [editingSpeciality, setEditingSpeciality] = useState<Speciality | null>(null);
    const [editingSection, setEditingSection] = useState<MedicalRecordSection | null>(null);

    const selectedSpeciality = useMemo(
        () => specialities.find(item => item.id === selectedSpecialityId) || null,
        [specialities, selectedSpecialityId]
    );

    const filteredSpecialities = useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) {
            return specialities;
        }

        return specialities.filter(item =>
            item.name.toLowerCase().includes(value)
            || String(item.status || '').toLowerCase().includes(value)
        );
    }, [specialities, search]);

    const loadData = async () => {
        const [specialityResponse, sectionResponse] = await Promise.all([
            props.getActiveSpeciality(),
            props.getMedicalRecordSections()
        ]);

        const loadedSpecialities = specialityResponse || [];
        const loadedSections = sectionResponse || [];

        setSpecialities(loadedSpecialities);
        setSections(loadedSections);

        const currentSelectedId = selectedSpecialityId || loadedSpecialities[0]?.id || null;
        setSelectedSpecialityId(currentSelectedId);
        syncSelectedDefaults(currentSelectedId, loadedSpecialities);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const syncSelectedDefaults = (specialityId: string | null, specialityList = specialities) => {
        const speciality = specialityList.find(item => item.id === specialityId);
        const defaults = (speciality?.defaultSections || [])
            .filter(section => section.canDisable)
            .map(section => String(section.id));

        setSelectedDefaultSectionIds(defaults);
    };

    const selectSpeciality = (speciality: Speciality) => {
        const specialityId = speciality.id || null;
        setSelectedSpecialityId(specialityId);
        syncSelectedDefaults(specialityId);
    };

    const openSpecialityModal = (speciality?: Speciality) => {
        setEditingSpeciality(speciality || null);
        specialityForm.resetFields();
        specialityForm.setFieldsValue({
            name: speciality?.name || '',
            status: speciality?.status || 'ACTIVO'
        });
        setModalMode('speciality');
    };

    const openSectionModal = (section?: MedicalRecordSection) => {
        setEditingSection(section || null);
        sectionForm.resetFields();
        sectionForm.setFieldsValue({
            name: section?.name || '',
            description: section?.description || '',
            status: section?.status || 'ACTIVO'
        });
        setModalMode('section');
    };

    const closeModal = () => {
        setModalMode(null);
        setEditingSpeciality(null);
        setEditingSection(null);
        specialityForm.resetFields();
        sectionForm.resetFields();
    };

    const handleSaveSpeciality = async () => {
        const values = await specialityForm.validateFields();
        const request = {
            id: editingSpeciality?.id || null,
            name: values.name,
            status: values.status || 'ACTIVO'
        };

        const response = editingSpeciality
            ? await props.putSpeciality(request)
            : await props.postSpeciality(request);

        if (response) {
            await loadData();
            closeModal();
        }
    };

    const handleSaveSection = async () => {
        const values = await sectionForm.validateFields();
        const request = {
            id: editingSection?.id || null,
            name: values.name,
            description: values.description || null,
            status: values.status
        };

        const response = editingSection
            ? await props.putMedicalRecordSection(request)
            : await props.postMedicalRecordSection(request);

        if (response) {
            await loadData();
            closeModal();
        }
    };

    const handleSaveDefaultSections = async () => {
        if (!selectedSpecialityId) {
            message.warning('Seleccione una especialidad');
            return;
        }

        const response = await props.putSpecialityDefaultSections({
            specialityId: selectedSpecialityId,
            medicalRecordSectionIds: selectedDefaultSectionIds
        });

        if (response) {
            await loadData();
        }
    };

    const specialityColumns = [
        {
            title: 'Especialidad',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, row: Speciality) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Tag color={(row.status || 'ACTIVO') === 'ACTIVO' ? 'green' : 'red'} style={{ width: 'fit-content' }}>
                        {row.status || 'ACTIVO'}
                    </Tag>
                </Space>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 96,
            align: 'right' as const,
            render: (_: unknown, row: Speciality) => (
                <Space>
                    <Button size="small" onClick={() => selectSpeciality(row)}>
                        Ver
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openSpecialityModal(row)} />
                </Space>
            )
        }
    ];

    const sectionColumns = [
        {
            title: 'Seccion',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, row: MedicalRecordSection) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    {row.description ? <Text type="secondary">{row.description}</Text> : null}
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (value: string) => (
                <Tag color={(value || 'ACTIVO') === 'ACTIVO' ? 'green' : 'red'}>{value || 'ACTIVO'}</Tag>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 64,
            align: 'right' as const,
            render: (_: unknown, row: MedicalRecordSection) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => openSectionModal(row)} />
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Gestion de especialidades</Title>
                    <Text type="secondary">Administra especialidades, secciones de ficha medica y sus secciones por defecto.</Text>
                </div>

                <Card>
                    <Row gutter={[12, 12]} align="bottom">
                        <Col xs={24} md={12}>
                            <Text strong>Buscar especialidad</Text>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Nombre o estado"
                                style={{ marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<ReloadOutlined />} block onClick={loadData}>
                                Actualizar
                            </Button>
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<PlusOutlined />} type="primary" block onClick={() => openSpecialityModal()}>
                                Nueva especialidad
                            </Button>
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<PlusOutlined />} block onClick={() => openSectionModal()}>
                                Nueva seccion
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Card title="Especialidades">
                            <Table
                                rowKey="id"
                                columns={specialityColumns}
                                dataSource={filteredSpecialities}
                                pagination={{ pageSize: 8 }}
                                size="middle"
                            />
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card
                            title="Secciones por defecto"
                            extra={
                                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDefaultSections} disabled={!selectedSpecialityId}>
                                    Guardar
                                </Button>
                            }
                        >
                            {selectedSpeciality ? (
                                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                                    <div>
                                        <Text type="secondary">Especialidad seleccionada</Text>
                                        <Title level={5} style={{ marginTop: 4, marginBottom: 0 }}>{selectedSpeciality.name}</Title>
                                    </div>
                                    <Checkbox.Group
                                        value={selectedDefaultSectionIds}
                                        onChange={(values) => setSelectedDefaultSectionIds(values.map(String))}
                                        style={{ width: '100%' }}
                                    >
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {sections.map(section => (
                                                <Checkbox key={section.id} value={section.id}>
                                                    <Space direction="vertical" size={0}>
                                                        <Text>{section.name}</Text>
                                                        {section.description ? <Text type="secondary">{section.description}</Text> : null}
                                                    </Space>
                                                </Checkbox>
                                            ))}
                                        </Space>
                                    </Checkbox.Group>
                                </Space>
                            ) : (
                                <Empty description="Seleccione una especialidad" />
                            )}
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card title="Secciones">
                            <Table
                                rowKey="id"
                                columns={sectionColumns}
                                dataSource={sections}
                                pagination={{ pageSize: 8 }}
                                size="middle"
                            />
                        </Card>
                    </Col>
                </Row>

                <Modal
                    open={modalMode === 'speciality'}
                    title={editingSpeciality ? 'Editar especialidad' : 'Nueva especialidad'}
                    className="tbc-admin-modal"
                    okText={editingSpeciality ? 'Actualizar' : 'Crear'}
                    cancelText="Cancelar"
                    onOk={handleSaveSpeciality}
                    onCancel={closeModal}
                    destroyOnHidden
                    forceRender
                >
                    <Form form={specialityForm} layout="vertical">
                        <Form.Item label="Nombre" name="name" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                            <Input placeholder="Cardiologia" />
                        </Form.Item>
                        <Form.Item label="Estado" name="status">
                            <Select
                                disabled={!editingSpeciality}
                                options={[
                                    { value: 'ACTIVO', label: 'ACTIVO' },
                                    { value: 'INACTIVO', label: 'INACTIVO' }
                                ]}
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={modalMode === 'section'}
                    title={editingSection ? 'Editar seccion' : 'Nueva seccion'}
                    className="tbc-admin-modal"
                    okText={editingSection ? 'Actualizar' : 'Crear'}
                    cancelText="Cancelar"
                    onOk={handleSaveSection}
                    onCancel={closeModal}
                    destroyOnHidden
                    forceRender
                >
                    <Form form={sectionForm} layout="vertical">
                        <Form.Item label="Nombre" name="name" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                            <Input placeholder="Historia obstetrica" />
                        </Form.Item>
                        <Form.Item label="Descripcion" name="description">
                            <Input.TextArea rows={3} placeholder="Detalle interno de la seccion" />
                        </Form.Item>
                        <Form.Item label="Estado" name="status" rules={[{ required: true, message: 'Seleccione el estado' }]}>
                            <Select
                                options={[
                                    { value: 'ACTIVO', label: 'ACTIVO' },
                                    { value: 'INACTIVO', label: 'INACTIVO' }
                                ]}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </Space>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({});

export default connect(mapStateToProps, {
    getActiveSpeciality,
    getMedicalRecordSections,
    postSpeciality,
    putSpeciality,
    postMedicalRecordSection,
    putMedicalRecordSection,
    putSpecialityDefaultSections
})(SpecialitySectionManagement);
